"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Target, Users, Lightbulb, BookMarked, ArrowLeft, ExternalLink } from "lucide-react";
import { getMaterialUrl } from "@/data/materials-registry";
import { getWorldCatUrl, getOpenLibraryUrl, getAmazonUrl } from "@/data/literature-links";
import { Separator } from "@/components/ui/separator";
import type { Lesson } from "@/contexts/lesson-context";

interface LessonContentViewerProps {
  lesson: Lesson;
  onBack: () => void;
}

function toDisplayText(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => toDisplayText(item)).filter(Boolean).join(" ").trim();
  }
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>)
      .map((item) => toDisplayText(item))
      .filter(Boolean)
      .join(" ")
      .trim();
  }
  return "";
}

export function LessonContentViewer({ lesson, onBack }: LessonContentViewerProps) {
  const content = lesson.content as any;

  return (
    <div className="h-full overflow-auto p-2 space-y-3">
      {/* Header — on blue panel background */}
      <div className="space-y-3 pb-4 border-b border-white/20">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="mb-2 text-white/90 hover:text-white hover:bg-white/20"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to lessons
        </Button>
        
        <div className="flex items-start justify-between gap-1">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {lesson.gradeLevel && (
                <Badge className="text-xs bg-[var(--mindup-pink)] text-white border-0">
                  {lesson.gradeLevel}
                </Badge>
              )}
              {lesson.unit && (
                <Badge className="text-xs bg-white/25 text-white border border-white/40">
                  Unit {lesson.unit}: {content?.unitTitle || ""}
                </Badge>
              )}
              {lesson.lessonNumber && (
                <Badge className="text-xs bg-white/25 text-white border border-white/40">
                  Lesson {lesson.lessonNumber}
                </Badge>
              )}
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-white">{lesson.title}</h1>
            <p className="text-sm text-white/85 mt-2">{lesson.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-white/80">
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span>{lesson.duration}</span>
          </div>
        </div>
      </div>

      {/* Concepts */}
      {lesson.concepts && lesson.concepts.length > 0 && (
        <Card className="gap-1 p-2 gap-0 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg flex items-center gap-2 text-[var(--mindup-navy)]">
              <Lightbulb className="w-5 h-5 text-[var(--mindup-orange)]" />
              Key Concepts
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-2">
              {lesson.concepts.map((concept: string, idx: number) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{concept}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Objectives */}
      {lesson.objectives && lesson.objectives.length > 0 && (
        <Card className="gap-1 p-2 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg flex items-center gap-2 text-[var(--mindup-navy)]">
              <Target className="w-5 h-5 text-[var(--mindup-deep-green)]" />
              Lesson Objectives
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-2">
              {lesson.objectives.map((objective, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Social-Emotional Learning */}
      {lesson.socialEmotionalLearning && lesson.socialEmotionalLearning.length > 0 && (
        <Card className="gap-1 p-2 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg flex items-center gap-2 text-[var(--mindup-navy)]">
              <Users className="w-5 h-5 text-[var(--mindup-pink)]" />
              Social & Emotional Learning Connections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {lesson.socialEmotionalLearning.map((sel: string, idx: number) => (
                <Badge key={idx} variant="secondary">{sel}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Materials */}
      {lesson.materials && lesson.materials.length > 0 && (
        <Card className="gap-1 p-2 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg text-[var(--mindup-navy)]">Materials & Preparation</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {lesson.materials.map((material, idx) => {
                const materialUrl = getMaterialUrl(material);
                return (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    {materialUrl ? (
                      <a
                        href={materialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary hover:underline"
                      >
                        {material}
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ) : (
                      <span>{material}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Brain Link */}
      {content?.brainLink && (
        <Card className="bg-[var(--mindup-blue-light)]/50 border-[var(--mindup-navy)]/20 p-2">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg text-[var(--mindup-navy)]">Brain Link</CardTitle>
            <CardDescription>
              The neuroscience behind this lesson
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{content.brainLink}</p>
          </CardContent>
        </Card>
      )}

      {/* Activities */}
      {content?.activities && content.activities.length > 0 && (
        <Card className="gap-1 p-2 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg text-[var(--mindup-navy)]">Lesson Activities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {content.activities.map((activity: any, idx: number) => (
              <div key={idx} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Step {activity.step}</Badge>
                  <h3 className="font-semibold text-base">{activity.title}</h3>
                </div>
                <ul className="space-y-2 ml-6">
                  {activity.instructions.map((instruction: string, instIdx: number) => (
                    <li key={instIdx} className="text-sm flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{instruction}</span>
                    </li>
                  ))}
                </ul>
                {idx < content.activities.length - 1 && (
                  <Separator className="my-4" />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Making Connections */}
      {content?.makingConnections && content.makingConnections.length > 0 && (
        <Card className="gap-1 p-2 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="text-lg text-[var(--mindup-navy)]">Making Connections</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {content.makingConnections.map((connection: string, idx: number) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{connection}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Following Up */}
      {content?.followingUp && (
        <Card className="gap-1 p-2 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg text-[var(--mindup-navy)]">Following Up</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {content.followingUp.mindupForHomeAndLife && (
              <div>
                <h4 className="font-semibold text-sm mb-2">MindUP for Home and Life</h4>
                <ul className="space-y-2">
                  {content.followingUp.mindupForHomeAndLife
                    .map((item: unknown, idx: number) => ({ idx, text: toDisplayText(item) }))
                    .filter((entry: { idx: number; text: string }) => entry.text.length > 0)
                    .map((entry: { idx: number; text: string }) => (
                      <li key={entry.idx} className="text-sm flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span>{entry.text}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {toDisplayText(content.followingUp.mindfulMePortfolio) && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Mindful Me Portfolio</h4>
                <p className="text-sm">{toDisplayText(content.followingUp.mindfulMePortfolio)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Literature List */}
      {content?.literatureList && content.literatureList.length > 0 && (
        <Card className="gap-1 p-2 border-[var(--mindup-navy)]/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-display text-lg flex items-center gap-2 text-[var(--mindup-navy)]">
              <BookMarked className="w-5 h-5 text-[var(--mindup-orange)]" />
              MindUP Literature List
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">Click titles to find in libraries or purchase</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {content.literatureList.map((book: string, idx: number) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 flex-1 min-w-0">
                    <a
                      href={getWorldCatUrl(book)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary font-medium hover:underline"
                    >
                      {book}
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                    <span className="text-muted-foreground text-xs">
                      <a href={getOpenLibraryUrl(book)} target="_blank" rel="noopener noreferrer" className="hover:underline">Open Library</a>
                      {" · "}
                      <a href={getAmazonUrl(book)} target="_blank" rel="noopener noreferrer" className="hover:underline">Amazon</a>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

